import { useEffect, useState } from "react";
import { LabelMolecule, TooltipMolecule } from "..";
import { IFileValidator, ITextValidator } from "../../../models/validator";
import { FileSelectionAreaMolecule, LegacyFileInputMolecule } from "../bestand-upload";
import { WeblocatieInvoerMolecule } from "../weblocatie-invoer";
import styles from "./styles.module.scss";

interface Props {
  label?: string;
  tooltip?: string | string[];
  required?: boolean;
  disabled?: boolean;
  inputRef?: (ref: HTMLButtonElement) => void;
  weblocatieInputRef?: (ref: HTMLInputElement) => void;

  fileUploadBlocked?: boolean;
  selectedFileName?: string;
  selectedInput?: File | string;
  validations?: IFileValidator[];
  onSelectFile: (file?: File | File[]) => void;
  onRemoveFile?: () => void;

  weblocatie?: string;
  weblocatieTooltip?: string | string[];
  weblocatieValidations?: ITextValidator[];
  onWeblocatieChange?: (url: string | undefined) => void;
  onWeblocatieHerindexatie?: () => void;
}

function useFileOrWeblocatie(props: Props) {
  const [error, setError] = useState<string>();
  const [urlValue, setUrlValue] = useState(props.weblocatie ?? "");
  const [urlError, setUrlError] = useState<string>();

  useEffect(() => {
    setUrlValue(props.weblocatie ?? "");
  }, [props.weblocatie]);

  const onFileChanged = (file?: File | File[]) => {
    const firstError = props.validations?.map((v: IFileValidator) => v.validate(file)).find((e) => e !== undefined);
    if (firstError) {
      setError(firstError);
      return;
    }
    setError(undefined);
    setUrlError(undefined);
    setUrlValue("");
    props.onWeblocatieChange?.(undefined);
    props.onSelectFile(file);
  };

  const onBlur = () => {
    if (!(props.selectedInput instanceof File) && typeof props.selectedInput !== "string") {
      onFileChanged();
    }
  };

  const onUrlBlur = () => {
    if (!urlValue) {
      setUrlError(
        props.selectedInput || props.selectedFileName
          ? undefined
          : "Voer een geldige link in die begint met https:// en verwijst naar een PDF of ZIP."
      );
      props.onWeblocatieChange?.(undefined);
      return;
    }
    const urlValidationError = props.weblocatieValidations
      ?.map((v) => v.validate(urlValue))
      .find((e) => e !== undefined);
    setUrlError(urlValidationError);
    if (urlValidationError) return;
    setError(undefined);
    if (urlValue === props.weblocatie) return;
    if (props.selectedFileName) props.onSelectFile(undefined);
    props.onWeblocatieChange?.(urlValue);
  };

  const onUrlClear = () => {
    setUrlValue("");
    setUrlError(undefined);
    props.onWeblocatieChange?.(undefined);
  };

  return {
    error,
    urlValue,
    urlError,
    onFileChanged,
    onBlur,
    onUrlBlur,
    onUrlClear,
    onUrlChange: setUrlValue,
  };
}

export function FileOrWeblocatieMolecule(props: Readonly<Props>) {
  const { error, urlValue, urlError, onFileChanged, onBlur, onUrlBlur, onUrlClear, onUrlChange } =
    useFileOrWeblocatie(props);
  const weblocatieEnabled = process.env.REACT_APP_WEBLOCATIE === "true";

  if (props.disabled && !props.selectedFileName && !(weblocatieEnabled && props.weblocatie)) {
    return <></>;
  }

  if (!weblocatieEnabled) {
    return (
      <LegacyFileInputMolecule
        selectedFileName={props.selectedFileName}
        disabled={props.disabled}
        error={error}
        onFileChanged={onFileChanged}
        onBlur={onBlur}
        inputRef={props.inputRef}
      />
    );
  }

  const dividerClass = error && urlError ? `${styles.divider} ${styles["divider--error"]}` : styles.divider;

  return (
    <div>
      <LabelMolecule id={"file_input_label"} label={props.label ?? "Document toevoegen"} required={props.required} />
      <p className={styles.subtitle}>Alleen PDF of ZIP</p>

      <div className={styles.blue_container}>
        <div className={styles.section_label_row}>
          <p className={styles.section_label}>Upload uw document</p>
          {props.tooltip && <TooltipMolecule id={"upload_section_tooltip"} content={props.tooltip} />}
        </div>

        <FileSelectionAreaMolecule
          fileUploadBlocked={props.fileUploadBlocked}
          selectedFileName={props.selectedFileName}
          disabled={props.disabled}
          error={error}
          weblocatie={props.weblocatie}
          onFileChanged={onFileChanged}
          onBlur={onBlur}
          onRemoveFile={props.onRemoveFile}
          inputRef={props.inputRef}
        />

        <div className={dividerClass}>
          <span>of</span>
        </div>

        <WeblocatieInvoerMolecule
          weblocatieTooltip={props.weblocatieTooltip}
          urlValue={urlValue}
          urlError={urlError}
          selectedFileName={props.selectedFileName}
          disabled={props.disabled}
          inputRef={props.weblocatieInputRef}
          onChange={onUrlChange}
          onBlur={onUrlBlur}
          onClear={onUrlClear}
          onHerindexatie={props.onWeblocatieHerindexatie}
        />
      </div>
    </div>
  );
}
